// angular
import {Component, OnInit, effect, inject, input} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
// material angular
import {MatIconModule} from "@angular/material/icon";
import {MatDialogRef} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
// components
import {SelectComponent} from "@app/core/components/select/select.component";
import {NestedSelectComponent} from "@app/core/components/nested-select/nested-select.component";
import {FiltersDialogFrameComponent} from "@app/core/components/filters-dialog-frame/filters-dialog-frame.component";
import {DatePickerComponent} from "@app/core/components/date-picker/date-picker.component";
import {InputComponent} from "@app/core/components/input/input.component";
// services
import {UserService} from "@app/shared/services/user.service";
import {OrderFiltersService} from "@app/+order/shared/services/order-filters.service";
import {BranchService} from "src/app/shared/services/branch.service";
import {CategoriesAndProductsTreeService} from "src/app/shared/services/categories-and-products-tree.service";
import {CustomerService} from "@app/+customer/shared/services/customer.service";
// types
import {SelectItemS} from "src/app/core/components/select/Item.type";
import {Seller} from "src/app/shared/types/seller.type";
import {IBranchInfo} from "src/app/shared/types/branch.type";
import {IProduct} from "src/app/shared/types/product.type";
import {NestedSelectItem} from "src/app/core/components/nested-select/nested-select-type";
import {ISaleLine} from "@app/+capillary/shared/types/line.type";
import {IUserData} from "@app/shared/types/employee.type";
import {StepEnum} from "@app/+order/shared/types/step.type";
import {PaymentMethodEnum} from "@app/+order/shared/types/payment-method.type";
import {PaymentStatusEnum} from "@app/+order/shared/types/payment-status.type";
import {DeliveryMethodEnum} from "@app/+order/shared/types/delivery-method.type";
import {NotPurchasedReasonEnum} from "@app/+order/shared/types/not-purchased-reason.type";
import {ICustomerForOrder} from "@app/+order/shared/types/order.type";
import {Role} from "@app/shared/types/role.enum";
// utils
import {removeFormNullValues} from "@app/shared/utils/removeFormNullValues";
import {generateSelectOptionsFromEnum} from "@app/shared/utils/generateSelectOptionsFromEnum";
import convertDateToISO from "src/app/shared/utils/convertDateToISO";
// constants
import {formGridStyle} from "src/app/shared/constants/css-classes";
import {
  answeredStatusOptions,
  archivedOptions,
  buyStatusOptions,
  customerStatusOptions,
  didWeContactOptions,
  purchaseTypeOptions,
} from "./order-list-filters-dialog-select-options";
// directive
import {OnlyNumberDirective} from "@app/core/directives/only-number.directive";
import {PreventFormSubmitDirective} from "@app/core/directives/form-enter-handler.directive";

@Component({
  selector: "app-order-list-filters-dialog",
  templateUrl: "./order-list-filters-dialog.component.html",
  styleUrls: ["./order-list-filters-dialog.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SelectComponent,
    MatIconModule,
    NestedSelectComponent,
    FiltersDialogFrameComponent,
    MatButtonModule,
    DatePickerComponent,
    InputComponent,
    OnlyNumberDirective,
    PreventFormSubmitDirective,
  ],
  providers: [OrderFiltersService],
})
export class OrderListFiltersDialogComponent implements OnInit {
  public Roles = Role;
  public user: IUserData;
  public userRoles: Role[];

  public formGridStyle = formGridStyle;
  public form: FormGroup;
  private _qParams: Record<string, string>;
  private _branchInfo: IBranchInfo;
  public allProducts: IProduct[];
  public categoriesWithAllProductsTree: NestedSelectItem[] = [];
  public buyStatusOptions = buyStatusOptions;
  public answeredStatusOptions = answeredStatusOptions;
  public didWeContactOptions = didWeContactOptions;
  public purchaseTypeOptions = purchaseTypeOptions;
  public customerStatusOptions = customerStatusOptions;
  public archivedOptions = archivedOptions;
  public saleLineOptions: SelectItemS = [];

  public customersOptions: SelectItemS = [];
  public representativesOptions: SelectItemS = [];
  public sellersOptions: SelectItemS = [];

  public stepOptions = generateSelectOptionsFromEnum(StepEnum, {
    renderRemoveOption: true,
  });
  public paymentMethodOptions = generateSelectOptionsFromEnum(
    PaymentMethodEnum,
    {renderRemoveOption: true}
  );
  public paymentStatusOptions = generateSelectOptionsFromEnum(
    PaymentStatusEnum,
    {renderRemoveOption: true}
  );
  public deliveryMethodOptions = generateSelectOptionsFromEnum(
    DeliveryMethodEnum,
    {renderRemoveOption: true}
  );
  public notPurchasedReasonOptions = generateSelectOptionsFromEnum(
    NotPurchasedReasonEnum as any,
    {renderRemoveOption: true}
  );

  // dependencies
  private _branchesService = inject(BranchService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _spinner = inject(NgxSpinnerService);
  private _orderFiltersService = inject(OrderFiltersService);
  private _userService = inject(UserService);
  private _categoriesAndProductsTreeService = inject(
    CategoriesAndProductsTreeService
  );
  dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef);
  private _customerService = inject(CustomerService);

  constructor() {
    effect(() => {
      this.userRoles = this._userService.roles();
    });
  }

  ngOnInit() {
    this.form = this._getFormInitialValue();
    this._userService.user$.subscribe((user) => (this.user = user));
    this._activatedRoute.queryParams.subscribe((qParams) => {
      this._qParams = qParams;
    });
    this._branchesService.branchInfo$.subscribe((info) => {
      this._branchInfo = info;
      this._getProducts();
    });
    this._patchFormFieldsValues();
    this._orderFiltersService.getCustomers().subscribe((res) => {
      this._generateCustomerOptions(res);
      this._generateRepresentativeOptions(res);
    });

    // get sale lines data
    this._customerService.getSaleLines().subscribe((res) => {
      this._generateSaleLineOptions(res.data);
    });

    this._orderFiltersService
      .getSellers()
      .subscribe((res) => this._generateSellerOptions(res));
  }

  private _getFormInitialValue() {
    return new FormGroup({
      step: new FormControl([]),
      code: new FormControl([]),
      hp_invoice_code: new FormControl([]),
      sales_line: new FormControl(""),
      payment_method: new FormControl(""),
      payment_status: new FormControl(""),

      bought: new FormControl(""),
      new_customer: new FormControl(""),
      did_we_contact: new FormControl(""),
      answered: new FormControl(""),
      archived: new FormControl(""),
      in_person_order: new FormControl(""),

      product: new FormControl(""),
      seller_id: new FormControl(""),
      customer_id: new FormControl(""),
      person_id: new FormControl(""),

      not_purchased_reason: new FormControl(""),
      delivery_method: new FormControl(""),

      created_date_min: new FormControl(null),
      created_date_max: new FormControl(null),
      delivery_date_min: new FormControl(null),
      delivery_date_max: new FormControl(null),
      // payment_date_min: new FormControl(null),
      // payment_date_max: new FormControl(null),
    });
  }

  private _getProducts() {
    this._categoriesAndProductsTreeService
      .getCategoriesAndProductsTree(this._branchInfo.warehouses?.[0]?.id)
      .subscribe((res) => {
        this.allProducts = res?.products || [];
        this.categoriesWithAllProductsTree =
          res?.categoriesWithAllProducts || [];
      });
  }

  private _generateSaleLineOptions(res: ISaleLine[]) {
    this.saleLineOptions = [{value: "", key: "همه"}].concat(
      res.map((item) => ({
        value: item.id,
        key: item.line_number + " - " + item.title,
      }))
    );
  }

  private _generateRepresentativeOptions(representatives: ICustomerForOrder[]) {
    this.representativesOptions = [{value: "", key: "همه"}].concat(
      representatives
        .map((c) => {
          return c.people.map((item) => {
            return {
              key: `${item?.profile?.first_name || ""} ${item?.profile?.last_name || ""}`,
              value: item.id,
            };
          });
        })
        .flat()
    );
  }

  private _generateCustomerOptions(res: ICustomerForOrder[]) {
    this.customersOptions = [{value: "", key: "همه"}].concat(
      res.map((item) => ({
        key: item.title,
        value: item.id,
      }))
    );
  }

  private _generateSellerOptions(res: Seller[]) {
    this.sellersOptions = [{value: "", key: "همه"}].concat(
      res.map((item) => ({
        value: item?.id,
        key: item.profile?.first_name?.concat(` ${item?.profile?.last_name}`),
      }))
    );
  }

  private _patchFormFieldsValues() {
    if (Object.keys(this._qParams).length) {
      setTimeout(() => {
        this.form.patchValue({
          ...this._qParams,
          delivery_date_min: this._qParams["delivery_date_min"]
            ? this._qParams["delivery_date_min"]
            : null,
          delivery_date_max: this._qParams["delivery_date_max"]
            ? this._qParams["delivery_date_max"]
            : null,
          created_date_min: this._qParams["created_date_min"]
            ? this._qParams["created_date_min"]
            : null,
          created_date_max: this._qParams["created_date_max"]
            ? this._qParams["created_date_max"]
            : null,
          step: this._qParams["step"] ? this._qParams["step"].split(",") : [],
        });
      }, 100);
    }
  }

  public reset() {
    this.form.reset(this._getFormInitialValue().value);
    this.submit();
  }

  public submit() {
    const createdMin = this.form.value.created_date_min;
    const createdMax = this.form.value.created_date_max;
    const deliveryMin = this.form.value.delivery_date_min;
    const deliveryMax = this.form.value.delivery_date_max;

    const values = {
      ...this.form.value,
      delivery_date_min: deliveryMin ? convertDateToISO(deliveryMin) : null,
      delivery_date_max: deliveryMax ? convertDateToISO(deliveryMax) : null,
      created_date_min: createdMin ? convertDateToISO(createdMin) : null,
      created_date_max: createdMax ? convertDateToISO(createdMax) : null,
      step: this.form.value.step.join(","),
    };

    const sort = this._qParams?.["sort"];
    const queryParams = removeFormNullValues({
      ...values,
      ...(sort ? {sort} : {}),
    });
    if (JSON.stringify(this._qParams) === JSON.stringify(queryParams)) {
      this.dialogRef.close();
      return;
    }
    this._spinner.show();
    this._router.navigate([], {queryParams}).then(() => {
      this.dialogRef.close();
    });
  }
}
